import React from 'react';
import { Box, Button, Typography } from '@mui/material';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { EmptyMark } from '../components/ui';
import { useAuth } from '../contexts/AuthContext';
import { sbg, typeStyle } from '../theme/sbgTokens';

/**
 * Rendered inside the app shell for any unknown route. A calm, full-page
 * empty state (book §11.15): the quiet mark, what happened, the address we
 * couldn't find, and the way home.
 */
const NotFound: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { profile } = useAuth();

  // Mirrors DefaultRedirect in App.tsx: employees can't open /dashboard.
  const home =
    profile?.role === 'employee'
      ? { to: '/org-chart', label: 'Go to org chart' }
      : { to: '/dashboard', label: 'Go to dashboard' };
  // React Router keeps its own history index in history.state, and replace
  // navigations (RequireAuth → /login → back here) keep it at 0. Only offer
  // "Go back" when there's an earlier entry from this app in this tab.
  const historyIndex = (window.history.state as { idx?: number } | null)?.idx ?? 0;
  const canGoBack = historyIndex > 0;

  return (
    <Box
      component="section"
      aria-labelledby="not-found-title"
      sx={{
        display: 'grid',
        placeItems: 'center',
        minHeight: { xs: 'auto', sm: 'min(70vh, 640px)' },
        py: { xs: 6, sm: 4 },
      }}
    >
      <Box sx={{ display: 'grid', justifyItems: 'center', textAlign: 'center', width: '100%', maxWidth: 480 }}>
        <EmptyMark size={48} sx={{ mb: 3 }} />
        <Typography id="not-found-title" variant="h2" component="h1" sx={{ color: sbg.text.primary }}>
          Page not found
        </Typography>
        <Typography variant="body1" sx={{ color: sbg.text.secondary, mt: 1, maxWidth: '44ch' }}>
          We couldn’t find{' '}
          <Box
            component="code"
            sx={{
              ...typeStyle('mono-sm'),
              color: sbg.text.primary,
              bgcolor: sbg.bg.surface,
              border: `1px solid ${sbg.border.subtle}`,
              borderRadius: sbg.radius.sm,
              px: 0.75,
              wordBreak: 'break-all',
            }}
          >
            {location.pathname}
          </Box>
          . It may have moved, or the link may be out of date.
        </Typography>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column-reverse', sm: 'row' },
            justifyContent: 'center',
            gap: 1,
            mt: 3,
            width: { xs: '100%', sm: 'auto' },
          }}
        >
          {canGoBack && (
            <Button variant="outlined" startIcon={<ArrowLeft />} onClick={() => navigate(-1)}>
              Go back
            </Button>
          )}
          <Button variant="contained" component={RouterLink} to={home.to}>
            {home.label}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default NotFound;
